// src/components/ClaimDetailPage.jsx
// Página individual de un reclamo completo (/claim-status/claim/:groupId),
// pensada para abrirse en una pestaña nueva desde el botón "Ver detalle
// completo" del directorio (ClaimsDirectory.jsx) — URL propia, compartible,
// sin depender de que el directorio siga cargado. El link que trae hasta acá
// ya incluye ?checks=id1,id2,... con TODOS los cheques del reclamo.
//
// Layout (UX_AUDIT_PLAN.md, paso 2): cabecera en dos filas (ClaimHeader +
// franja de estado), barra de secciones (ClaimTabs) y una sección visible a la
// vez: Pasos, Cheques, Cobro CSR (ClaimFinancials), Agenda, Documentos,
// Archivos, Casos y notas y Datos (ClaimDataPanel). Cada sección se monta la
// primera vez que se abre y después se conserva oculta, para no perder un
// formulario a medias al cambiar de pestaña. Los reclamos sin cheques y los
// que sí los tienen comparten el mismo esqueleto.
// Mismo gate de auth que ClaimStatusGate: sin sesión muestra el login unificado.
import React, { useContext, useEffect, useState } from 'react';
import WorkspacePreferences from './WorkspacePreferences';
import { useParams, useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getCheckForDirectoryById, getCheckSummaryById, deleteClaimRecordCascade } from '../services/claimService';
import { getClaimRecordById } from '../services/claimRecordService';
import { getClaimEvents } from '../services/claimEventService';
import { hasRealCrmLink, crmLinkFor } from '../utils/claimHelpers';
import Login from './Login';
import InitialPasswordChange from './InitialPasswordChange';
import ClaimAttachments from './Dashboard/ClaimAttachments';
import ClaimChecksTab from './Dashboard/ClaimChecksTab';
import ClaimRecordModal from './Dashboard/ClaimRecordModal';
import useStatusCatalog from '../hooks/useStatusCatalog';
import ClaimDocuments from './Dashboard/ClaimDocuments';
import ClaimFlowPanel from './Dashboard/ClaimFlowPanel';
import ClaimCases from './Dashboard/ClaimCases';
import ClaimSchedule from './Dashboard/ClaimSchedule';
import ConfirmModal from './common/ConfirmModal';
import ClaimHeader, { ClaimStatusStrip } from './ClaimDetail/ClaimHeader';
import ClaimTabs, { sectionsFor } from './ClaimDetail/ClaimTabs';
import ClaimFinancials from './ClaimDetail/ClaimFinancials';
import ClaimDataPanel from './ClaimDetail/ClaimDataPanel';
import { db } from '../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { LanguageContext } from '../App';
import './Dashboard/Dashboard.css';
import './Dashboard/ClaimsDirectory.css';
import './ClaimStatus.css';
import { hasAccess } from '../utils/claimAccess';

// Lo que se MUESTRA del reclamo sale del claimRecord (dato vigente); el cheque
// solo trae una copia tomada cuando se creó, que queda vieja si después se
// corrige el reclamo (así apareció una Date of Loss 02/15 en vez de 03/15,
// reunión 22-sep). La copia del cheque queda de respaldo cuando el padre no
// tiene el dato. Los cheques ya creados guardan su propia copia de
// clientName/company/etc. tomada al firmar el Closing Statement, por diseño:
// editar el reclamo corrige solo el claimRecord, no retroactivamente los cheques.
const CLAIM_DISPLAY_FIELDS = ['claimNumber', 'clientName', 'cause', 'insuranceCompanyName', 'clientEmail', 'clientPhone', 'clientAddress', 'companyName', 'adjusterName', 'policyNumber', 'incidentDate'];
const mergedRecord = (check, record) => {
  if (!check && !record) return null;
  const merged = { ...(check || {}), ...(record || {}) };
  if (check && record) CLAIM_DISPLAY_FIELDS.forEach(key => { if (!record[key] && check[key]) merged[key] = check[key]; });
  return merged;
};

const ClaimDetailPage = () => {
  const statusCatalog = useStatusCatalog();
  const { claimId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // De dónde se abrió la ficha (la lista con sus filtros): "Volver" regresa ahí.
  const location = useLocation();
  const backTo = typeof location.state?.from === 'string' && location.state.from.startsWith('/') ? location.state.from : '/dashboard';
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const es = language === 'es';
  const { isAuthenticated, isAdmin, isAdjuster, userAdjusterName, userCanViewAllClaims, mustChangePassword, userRoleKey, can } = useAuth();
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Código del error de carga ('notfound' | 'access'); el texto se calcula al pintar para que
  // cambiar de idioma no obligue a volver a cargar (y a perder lo escrito en) la ficha.
  const [error, setError] = useState('');
  const [claimRecord, setClaimRecord] = useState(null);
  // Si el reclamo padre no carga, la ficha lo dice (antes quedaba con paneles en blanco):
  // 'load' = falló la lectura (con Reintentar), 'noparent' = el cheque no tiene expediente.
  const [recordIssue, setRecordIssue] = useState(null);
  const [recordReload, setRecordReload] = useState(0);
  const [deletingClaim, setDeletingClaim] = useState(false);
  // Sube al guardar datos de un paso: remonta ClaimDocuments para que la
  // SPOL relea los montos del estimado (stepData de rs_pol).
  const [docsVersion, setDocsVersion] = useState(0);
  // Cambios hechos desde Documentos (ej. póliza escrita en el Closing): se
  // reflejan en la ficha sin remontar el panel (no cierra el formulario abierto).
  const mergeClaimRecord = changes => setClaimRecord(current => (current ? { ...current, ...changes } : current));
  const handleClaimChange = changes => {
    setClaimRecord(current => (current ? { ...current, ...changes } : current));
    setDocsVersion(v => v + 1);
  };
  const [showEdit, setShowEdit] = useState(false);
  const [nextEvent, setNextEvent] = useState(null);
  const [pendingDeleteClaim, setPendingDeleteClaim] = useState(false);

  // Qué puede ver y editar el usuario. Puede editar el claim padre quien lo
  // puede escribir en firestore.rules: admin, o el ajustador dueño (su propio
  // nombre, o cualquiera de su empresa si es supervisor con viewAllClaims).
  const primary = checks[0];
  const accessRecord = primary || claimRecord;
  const canEditClaim = !!accessRecord && (isAdmin || (isAdjuster && (userCanViewAllClaims || hasAccess(accessRecord, userAdjusterName))));
  // Sin cheques del modelo anterior, las secciones de trabajo son solo para quien
  // puede editar; quien no puede ve únicamente los datos del reclamo.
  const showTabs = !!primary || canEditClaim;
  const sections = sectionsFor({ hasChecks: !!primary, canEdit: canEditClaim, canSeeEarnings: isAdmin || can('viewEarnings') });

  // La sección abierta vive en la URL (?section=) para que recargar o volver a
  // la ficha la recupere; sin ella se abre en Pasos. `checks` se conserva.
  const sectionParam = searchParams.get('section');
  const activeSection = sections.some(([id]) => id === sectionParam) ? sectionParam : 'claim-steps';
  const setActiveSection = id => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (id === 'claim-steps') next.delete('section'); else next.set('section', id);
    return next;
  }, { replace: true });

  // Cada sección se monta al abrirla por primera vez y después se queda montada
  // (oculta): así no se cargan las que nadie abre, pero tampoco se pierde un
  // formulario a medias al cambiar de pestaña.
  const [visited, setVisited] = useState(() => new Set());
  useEffect(() => { setVisited(new Set()); }, [claimId]);
  useEffect(() => {
    setVisited(prev => (prev.has(activeSection) ? prev : new Set(prev).add(activeSection)));
  }, [activeSection]);
  const panel = (id, render) => (id === activeSection || visited.has(id)
    ? <div id={id} className="claim-anchor" hidden={activeSection !== id}>{render()}</div>
    : null);

  // ?checks=id1,id2,... trae todos los cheques del reclamo; si falta (link
  // viejo o entrada directa por URL) se cae a solo el id de la ruta.
  const checkIds = (searchParams.get('checks') || claimId || '')
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);

  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    setLoading(true);
    setError('');
    setRecordIssue(null);
    setClaimRecord(null);
    setChecks([]);
    // Sin ?checks= en la URL: es un claimRecordId directo, un reclamo que
    // todavía no tiene ningún cheque (link armado por ClaimsDirectory.jsx
    // → sección "Pendientes de Closing Statement", que no tiene cheques
    // que pasar). Se resuelve distinto: no hay checks que buscar, se carga
    // el claimRecord directo — ver docs/archivo/REFACTOR_MYCLAIMS_CLAIMSTATUS.md,
    // portado 2026-09-20.
    if (!searchParams.get('checks')) {
      getClaimRecordById(claimId)
        .then(record => {
          if (!active) return;
          if (!record) {
            setError('notfound');
          } else {
            setChecks([]);
            setClaimRecord(record);
          }
        })
        .catch(err => {
          console.error('Error loading claim record:', err);
          if (active) setError('access');
        })
        .finally(() => { if (active) setLoading(false); });
      return () => { active = false; };
    }
    if (checkIds.length === 0) { setLoading(false); return undefined; }
    Promise.all(checkIds.map(id => (userRoleKey === 'csr_assistant' ? getCheckSummaryById(id) : getCheckForDirectoryById(id))))
      .then(results => {
        if (!active) return;
        const found = results.filter(Boolean);
        if (found.length === 0) {
          setError('notfound');
        } else {
          setChecks(found);
        }
      })
      .catch(err => {
        console.error('Error loading claim detail:', err);
        if (active) setError('access');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, searchParams.get('checks'), isAuthenticated]);

  // Los campos nuevos del spec del cliente (propiedad/dueños, aseguradora,
  // coberturas, mortgage) viven en el claimRecord PADRE, no en cada check
  // — se piden aparte para no duplicar ~30 campos en cada documento de
  // checks (ver claimRecordService.js).
  const checksParentId = checks[0]?.claimRecordId;
  useEffect(() => {
    // checks.length === 0 cubre dos casos: todavía no cargaron (flujo normal,
    // este efecto se re-ejecuta cuando checksParentId aparece) o el flujo "sin
    // cheques" de arriba, que ya seteó claimRecord directo — acá no hay que
    // tocarlo de nuevo (ni resetearlo a null).
    if (checks.length === 0) return;
    if (!checksParentId) { setClaimRecord(null); setRecordIssue('noparent'); return undefined; }
    let active = true;
    setRecordIssue(null);
    getClaimRecordById(checksParentId)
      .then(record => { if (active) { setClaimRecord(record); if (!record) setRecordIssue('load'); } })
      .catch(err => {
        console.error('Error loading parent claim record:', err);
        if (active) { setClaimRecord(null); setRecordIssue('load'); }
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checksParentId, recordReload]);

  // Próxima cita para la franja de estado — se pide acá, aparte, en vez de
  // esperar a que ClaimSchedule cargue las suyas (que maneja su propia
  // sección), para que el resumen de arriba no dependa de qué pestaña esté abierta.
  const parentId = checksParentId || claimRecord?.id;
  // Se relee al entrar y al salir de Agenda: allí se agendan y confirman citas, y la franja
  // seguiría diciendo "Sin citas" hasta recargar la página.
  const onSchedule = activeSection === 'claim-schedule';
  useEffect(() => {
    if (!parentId) { setNextEvent(null); return undefined; }
    let active = true;
    getClaimEvents(parentId)
      .then(events => {
        if (!active) return;
        const now = Date.now();
        const upcoming = events
          .filter(e => e.date && new Date(`${e.date}T${e.time || '23:59'}`).getTime() >= now)
          .sort((a, b) => `${a.date}T${a.time || ''}`.localeCompare(`${b.date}T${b.time || ''}`));
        setNextEvent(upcoming[0] || null);
      })
      .catch(err => { console.error('Error loading upcoming event:', err); if (active) setNextEvent(null); });
    return () => { active = false; };
  }, [parentId, onSchedule]);

  if (!isAuthenticated) {
    return <Login />;
  }

  if (!isAdmin && mustChangePassword) {
    return <InitialPasswordChange />;
  }

  const info = mergedRecord(primary, claimRecord);
  // Registro para el bloque de datos: siempre con el id del reclamo PADRE (el
  // `id` de un cheque es el del cheque).
  const dataRecord = info ? { ...info, id: parentId } : null;
  const crmLink = primary && hasRealCrmLink(primary) ? crmLinkFor(primary) : null;

  const handleDeleteClaim = async () => {
    if (!claimRecord || deletingClaim) return;
    setDeletingClaim(true);
    try {
      const result = await deleteClaimRecordCascade(claimRecord.id);
      toast.success(es
        ? `Reclamo eliminado (${result.deletedChecks} cheque(s) incluidos)`
        : `Claim deleted (${result.deletedChecks} check(s) included)`);
      navigate(backTo);
    } catch (err) {
      console.error(err);
      toast.error(es ? 'No se pudo eliminar' : 'Could not delete');
      setPendingDeleteClaim(false);
    } finally {
      setDeletingClaim(false);
    }
  };

  // Portado de MyClaims.jsx al fusionar My Claims con Claim Status
  // 2026-09-20 — quitar un flag de calidad de datos (crmLinkInvalid /
  // oaSyncFailed) una vez corregido a mano, si no queda pegado para siempre.
  const clearFlag = async field => {
    if (!claimRecord) return;
    try {
      await updateDoc(doc(db, 'claimRecords', claimRecord.id), { [field]: false });
      setClaimRecord(current => (current ? { ...current, [field]: false } : current));
    } catch (err) {
      console.error(err);
      toast.error(es ? 'No se pudo quitar el estado' : 'Could not clear the flag');
    }
  };

  const errorText = error === 'notfound'
    ? (es ? 'No se encontró este reclamo.' : 'This claim was not found.')
    : error ? (es ? 'No tienes acceso a este reclamo o ocurrió un error.' : 'You do not have access to this claim, or an error occurred.') : '';

  const dataPanel = dataRecord && <ClaimDataPanel claimRecord={claimRecord} crmLink={crmLink} language={language} />;

  return (
    <div className="dashboard-modern claim-status-page cd-detail-page">
      <div className="cd-container cd-detail-container">
        <div className="workspace-detail-toolbar"><Link to={backTo} className="cd-detail-back">
          <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
          {es ? 'Volver a reclamos' : 'Back to claims'}
        </Link><WorkspacePreferences /></div>

        {loading && (
          <div className="cd-loading">
            <div className="spinner"></div>
            <p>{es ? 'Cargando reclamo...' : 'Loading claim...'}</p>
          </div>
        )}

        {!loading && error && (
          <div className="cd-error">
            <i className="fa-solid fa-exclamation-triangle" aria-hidden="true"></i> {errorText}
          </div>
        )}

        {!loading && !error && info && (
          <>
            <ClaimHeader info={info} dataRecord={dataRecord} claimRecord={claimRecord} statusCatalog={statusCatalog} language={language}
              canEdit={canEditClaim} isAdmin={isAdmin} onEdit={() => setShowEdit(true)}
              onDelete={() => setPendingDeleteClaim(true)} onClearFlag={clearFlag} />

            {recordIssue && !claimRecord && (
              <div className="cd-error" role="alert">
                <i className="fa-solid fa-exclamation-triangle" aria-hidden="true"></i>{' '}
                {recordIssue === 'load'
                  ? (es ? 'No se pudo cargar el expediente del reclamo. Sin él, los pasos, documentos y edición no están disponibles.' : 'The claim file could not be loaded. Without it, steps, documents and editing are unavailable.')
                  : (es ? 'Este cheque no está asociado a un expediente de reclamo.' : 'This check is not linked to a claim file.')}{' '}
                {recordIssue === 'load' && (
                  <button type="button" className="cd-btn" onClick={() => setRecordReload(n => n + 1)}>
                    <i className="fa-solid fa-rotate-right" aria-hidden="true"></i> {es ? 'Reintentar' : 'Retry'}
                  </button>
                )}
              </div>
            )}

            {showTabs ? (
              <>
                <ClaimStatusStrip claimRecord={claimRecord} checks={checks} nextEvent={nextEvent} language={language} />
                <ClaimTabs language={language} sections={sections} activeSection={activeSection} onSectionChange={setActiveSection} />

                {/* Reclamo del flujo de pasos: lo guía su paso actual. Los cheques
                    de la Fase 4 viven dentro del panel de pasos, cada uno con su recorrido. */}
                {panel('claim-steps', () => claimRecord && (
                  <ClaimFlowPanel key={claimRecord.id} claimRecord={claimRecord} isAdmin={isAdmin} language={language} onClaimChange={handleClaimChange} />
                ))}
                {panel('claim-checks', () => claimRecord && (
                  <ClaimChecksTab key={claimRecord.id} claimRecord={claimRecord} isAdmin={isAdmin} language={language} active={activeSection === 'claim-checks'} />
                ))}
                {primary && panel('claim-payments', () => (
                  <ClaimFinancials checks={checks} setChecks={setChecks} isAdmin={isAdmin} language={language} />
                ))}
                {/* Fases 1-3 del documento del cliente: agendar inspección / RS /
                    mediación, subir evidencias y llevar casos y notas. */}
                {panel('claim-schedule', () => <ClaimSchedule claimRecordId={parentId} claimRecord={claimRecord} />)}
                {canEditClaim && panel('claim-documents', () => claimRecord && (
                  <ClaimDocuments key={docsVersion} claimRecord={claimRecord} language={language} isAdmin={isAdmin} checks={checks} onClaimChange={mergeClaimRecord} />
                ))}
                {panel('claim-files', () => <ClaimAttachments claimRecordId={parentId} />)}
                {panel('claim-notes', () => (
                  <ClaimCases
                    claimRecordId={parentId}
                    canWrite={canEditClaim}
                    isAdmin={isAdmin}
                    claimMeta={{ claimNumber: info.claimNumber, clientName: info.clientName }}
                  />
                ))}
                {panel('claim-details', () => dataPanel)}
              </>
            ) : dataPanel}
          </>
        )}

        {/* Editar y eliminar valen para cualquier reclamo, con o sin cheques. */}
        {showEdit && claimRecord && (
          <ClaimRecordModal
            existingRecord={claimRecord}
            onClose={() => setShowEdit(false)}
            // Guarda el registro devuelto por el formulario: releer por el
            // claimRecordId de los cheques dejaba la ficha en blanco en los
            // reclamos sin cheques (no tienen ese id).
            onSaved={record => { if (record) setClaimRecord(current => ({ ...current, ...record })); setDocsVersion(v => v + 1); }}
          />
        )}
        {claimRecord && (
          <ConfirmModal
            open={pendingDeleteClaim}
            icon="fa-trash"
            danger
            title={es ? 'Eliminar reclamo' : 'Delete claim'}
            message={es
              ? `¿Eliminar "${claimRecord.claimNumber}"? Esto también elimina sus ${checks.length} cheque(s) y la deuda de operador de los ya archivados. No se puede deshacer.`
              : `Delete "${claimRecord.claimNumber}"? This also deletes its ${checks.length} check(s) and the operator debt of any already archived. This cannot be undone.`}
            confirmLabel={es ? 'Eliminar todo' : 'Delete everything'}
            cancelLabel={es ? 'Cancelar' : 'Cancel'}
            onConfirm={handleDeleteClaim}
            onCancel={() => setPendingDeleteClaim(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ClaimDetailPage;
